import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import Disclaimer from "@/components/Disclaimer";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const FOOTER_COLS = [
  { h: "Product", l: [["Features", "/#features"], ["How It Works", "/#how"], ["Trust", "/#trust"]] },
  { h: "Company", l: [["About", "/about"], ["Contact", "/contact"]] },
  { h: "Legal", l: [["Privacy Policy", "/privacy"], ["Terms", "/terms"], ["Disclaimer", "/disclaimer"]] },
  { h: "Resources", l: [["Chat", "/chat"], ["Sign in", "/auth"]] },
];

export default function PublicLayout({ title, eyebrow, children }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Disclaimer ribbon */}
      <div className="w-full bg-primary text-primary-foreground text-[12px]">
        <div className="max-w-7xl mx-auto px-4 py-2 text-center opacity-90">
          Educational guidance only · Not financial advice · Consult a qualified professional
        </div>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-7">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
            <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link>
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/auth?mode=register">
              <Button data-testid="public-start-chatting-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                Start Chatting <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Page header */}
      <section className="border-b border-border/60 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          {eyebrow && (
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">{eyebrow}</p>
          )}
          <h1 data-testid="page-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
            {title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="ig-prose text-[15.5px] leading-relaxed">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex flex-col md:flex-row gap-8 justify-between">
            <div className="max-w-sm">
              <Logo />
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Your AI Insurance Expert — built to make insurance feel less like fine print and more like a conversation.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm">
              {FOOTER_COLS.map((col) => (
                <div key={col.h}>
                  <div className="font-display font-semibold text-foreground mb-3">{col.h}</div>
                  <ul className="space-y-2 text-muted-foreground">
                    {col.l.map(([label, to]) => (
                      <li key={label}><Link to={to} className="hover:text-foreground transition-colors">{label}</Link></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <Disclaimer />
          <div className="mt-6 text-[12px] text-muted-foreground flex flex-col sm:flex-row gap-2 justify-between">
            <span>© {new Date().getFullYear()} InsureGenie. All rights reserved.</span>
            <span className="inline-flex items-center gap-1.5">
              <span role="img" aria-label="India">🇮🇳</span>
              Made in India · Built for Indian insurance customers
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
